/**
 * Ride session (SPEC §10): physics at 20 Hz, trainer control at 4 Hz, recorder at 1 Hz,
 * auto-pause, laps, crash-safe persistence every 10 s.
 */
import { db } from '@/db/db';
import { hub } from '@/devices/hub';
import { airDensity } from '@/physics/constants';
import { stepSpeed } from '@/physics/rider';
import type { Course, CourseSample } from '@/routes/course';
import { getSettings } from '@/store/settings';
import { totalMassKg, type RideMode, type Settings } from '@/store/settingsModel';
import { BIKE } from '@/physics/bike';
import { ErgProtection } from '@/trainer/ergProtection';
import type { LiveData } from '@/store/devices';
import type { SimParams } from '@/trainer/types';
import { newId, type Workout } from '@/workouts/model';
import type { ActiveRideSnapshot, Lap, RideSample } from './model';
import { Recorder } from './recorder';
import type { RunnerView } from '@/workouts/runner';

export const PHYSICS_DT_MS = 50;
export const CONTROL_PERIOD_MS = 250;
export const PERSIST_PERIOD_MS = 10_000;
export const AUTO_PAUSE_SEC = 3;

export type SessionStatus = 'running' | 'paused' | 'finished';
export type PauseReason = 'manual' | 'auto' | null;

/** What the workout runner (P3) tells the session each tick. */
export interface WorkoutDirective {
  /** ERG target (W) when the step has a target. */
  targetW: number | null;
  /** Free step / forced-VS step → no ERG. */
  free: boolean;
  forceVs: boolean;
  /** Step boundary crossed during this tick → auto-lap with this label. */
  lapLabel?: string;
  /** Trainer feel forced flat (20-min FTP test block). */
  flatGrade?: boolean;
  done: boolean;
}

export interface WorkoutHook {
  readonly workout: Workout;
  tick(dtSec: number, ctx: { cadenceRpm: number; powerW: number; paused: boolean }): WorkoutDirective;
  /** Elapsed workout time (s) for crash recovery. */
  readonly elapsedSec: number;
  view?(): RunnerView;
}

export interface VsInput {
  dt: number;
  cadenceRpm: number;
  powerW: number;
  trainerSpeedKmh: number;
  gradePct: number;
  altitudeM: number;
  draft: number;
  settings: Settings;
}

export interface VsOutput {
  sim?: SimParams;
  ergW?: number;
  gSend?: number;
  pReq?: number;
  gear: number;
}

/** Virtual shifting controller (P5). */
export interface VsHook {
  compute(input: VsInput): VsOutput;
  readonly gear: number;
  readonly ratio: number;
}

export interface SessionOptions {
  course: Course;
  mode: RideMode;
  workout?: WorkoutHook | null;
  vs?: VsHook | null;
  /** Draft reduction source (multiplayer, P7). */
  draft?: () => number;
  plannedId?: string;
  resume?: ActiveRideSnapshot;
  /** Disable timers (tests drive tick()). */
  manualClock?: boolean;
}

export interface SessionSnapshotView {
  id: string;
  status: SessionStatus;
  pauseReason: PauseReason;
  mode: RideMode;
  effectiveMode: RideMode;
  movingSec: number;
  distanceM: number;
  courseD: number;
  speedMps: number;
  powerW: number;
  power3s: number;
  cadenceRpm: number;
  hrBpm: number;
  altitudeM: number;
  gradePct: number;
  lap: number;
  routeLap: number;
  ergTarget: number;
  ergFactor: number;
  workoutTargetW: number | null;
  gear: number;
  ratio: number;
  draft: number;
  gSend?: number;
  pReq?: number;
  routeFinished: boolean;
  direction: 1 | -1;
  sample: CourseSample;
  workout?: RunnerView;
  workoutName?: string;
  workoutErg: boolean;
}

export class RideSession {
  readonly id: string;
  readonly startedAt: number;
  readonly course: Course;
  status: SessionStatus = 'running';
  pauseReason: PauseReason = null;
  mode: RideMode;
  ergTarget: number;
  workout: WorkoutHook | null;
  vs: VsHook | null;
  plannedId?: string;
  readonly recorder: Recorder;
  /** Game speed (m/s). */
  v = 0;
  distanceM = 0;
  /** Position along the course; may exceed length for loops (wrapped in sample()). */
  courseD = 0;
  routeLap = 1;
  direction: 1 | -1 = 1;
  routeFinished = false;
  movingSec = 0;
  sample: CourseSample;
  live: LiveData = { t: 0, powerW: 0, cadenceRpm: 0, hrBpm: 0, speedKmh: 0 };
  readonly ergProtection = new ErgProtection();
  ergFactor = 1;
  draft = 0;
  private readonly draftSource: (() => number) | null;
  private lastVs: VsOutput | null = null;
  private workoutDirective: WorkoutDirective | null = null;
  private zeroFor = 0;
  private power3: { t: number; p: number }[] = [];
  private lastTick = 0;
  private lastControl = 0;
  private lastPersist = 0;
  private nextRecordAt = 1;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly listeners = new Set<(s: RideSession) => void>();
  private readonly finishListeners = new Set<() => void>();
  private readonly manualClock: boolean;

  constructor(opts: SessionOptions) {
    this.course = opts.course;
    this.mode = opts.mode;
    this.workout = opts.workout ?? null;
    this.vs = opts.vs ?? null;
    this.draftSource = opts.draft ?? null;
    this.manualClock = !!opts.manualClock;
    if (opts.plannedId) this.plannedId = opts.plannedId;
    this.ergTarget = Math.round(getSettings().ftp * 0.6);
    if (opts.resume) {
      const r = opts.resume;
      this.id = r.rideId;
      this.startedAt = r.startedAt;
      this.recorder = new Recorder(r.startedAt, r);
      const last = r.samples[r.samples.length - 1];
      this.distanceM = last?.dist ?? r.distanceM;
      this.courseD = r.distanceM;
      this.movingSec = last?.el ?? 0;
      this.nextRecordAt = Math.floor(this.movingSec) + 1;
      this.routeLap = r.lap;
      if (r.plannedId) this.plannedId = r.plannedId;
    } else {
      this.id = newId('ride');
      this.startedAt = Date.now();
      this.recorder = new Recorder(this.startedAt);
    }
    this.sample = this.course.sample(this.courseD);
  }

  start(nowMs = performance.now()): void {
    this.lastTick = nowMs;
    if (this.manualClock) return;
    this.timer = setInterval(() => this.tick(), PHYSICS_DT_MS);
  }

  onUpdate(cb: (s: RideSession) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  onFinish(cb: () => void): () => void {
    this.finishListeners.add(cb);
    return () => this.finishListeners.delete(cb);
  }

  get effectiveMode(): RideMode {
    const d = this.workoutDirective;
    if (this.workout && d && !d.done) {
      if (d.forceVs) return 'vs';
      if (d.free || d.targetW === null) return getSettings().defaultFreeRideMode;
      return this.workoutErgEnabled ? 'erg' : 'vs';
    }
    return this.mode;
  }

  /** ERG on/off toggle for workouts (SPEC §7.2): off → VS, target shown only. */
  workoutErgEnabled = true;

  /** performance.now() of the last physics tick (for render extrapolation). */
  get lastTickMs(): number {
    return this.lastTick;
  }

  get power3s(): number {
    if (this.power3.length === 0) return 0;
    return this.power3.reduce((s, x) => s + x.p, 0) / this.power3.length;
  }

  // ---- controls ----

  togglePause(): void {
    if (this.status === 'finished') return;
    if (this.status === 'paused') this.resume();
    else this.pause('manual');
  }

  pause(reason: Exclude<PauseReason, null>): void {
    if (this.status !== 'running') return;
    this.status = 'paused';
    this.pauseReason = reason;
    this.emit();
  }

  resume(): void {
    if (this.status !== 'paused') return;
    this.status = 'running';
    this.pauseReason = null;
    this.zeroFor = 0;
    this.lastTick = performance.now();
    this.emit();
  }

  manualLap(): Lap {
    return this.recorder.lap('manual', Date.now(), this.distanceM, this.movingSec);
  }

  /** Run the trainer control step on the next tick (e.g. right after a shift, SPEC §6.3: felt within ~0.5 s). */
  requestControlUpdate(): void {
    this.lastControl = 0;
  }

  adjustErg(deltaW: number): void {
    this.ergTarget = Math.max(0, Math.min(2000, this.ergTarget + deltaW));
    this.emit();
  }

  setMode(mode: RideMode): void {
    this.mode = mode;
    this.ergProtection.reset();
    this.emit();
  }

  turnAround(): void {
    this.direction = this.direction === 1 ? -1 : 1;
    this.routeFinished = false;
    this.emit();
  }

  // ---- main loop ----

  tick(nowMs = performance.now()): void {
    if (this.status === 'finished') return;
    const dt = Math.min(1, Math.max(0, (nowMs - this.lastTick) / 1000));
    this.lastTick = nowMs;
    const settings = getSettings();
    this.live = hub.live();
    const { powerW, cadenceRpm } = this.live;

    // 3 s power average
    const wall = Date.now();
    this.power3.push({ t: wall, p: powerW });
    while (this.power3.length && wall - this.power3[0]!.t > 3000) this.power3.shift();

    // Auto-pause (SPEC §10.1): power = 0 and cadence = 0 for 3 s
    if (powerW <= 0 && cadenceRpm <= 0) this.zeroFor += dt;
    else this.zeroFor = 0;
    if (settings.autoPause) {
      if (this.status === 'running' && this.zeroFor >= AUTO_PAUSE_SEC) this.pause('auto');
      else if (this.status === 'paused' && this.pauseReason === 'auto' && (powerW > 0 || cadenceRpm > 0))
        this.resume();
    }

    this.draft = this.draftSource ? Math.max(0, Math.min(0.4, this.draftSource())) : 0;

    if (this.status === 'running' && dt > 0) {
      this.stepPhysics(dt, powerW, settings);
      this.movingSec += dt;
      if (this.workout) {
        const d = this.workout.tick(dt, { cadenceRpm, powerW, paused: false });
        this.workoutDirective = d;
        if (d.lapLabel !== undefined) {
          this.recorder.lap('interval', Date.now(), this.distanceM, this.movingSec, d.lapLabel);
        }
      }
      while (this.movingSec + 1e-6 >= this.nextRecordAt) {
        this.record();
        this.nextRecordAt += 1;
      }
    }

    if (nowMs - this.lastControl >= CONTROL_PERIOD_MS) {
      const dtc = this.lastControl
        ? Math.min(1, (nowMs - this.lastControl) / 1000)
        : CONTROL_PERIOD_MS / 1000;
      this.lastControl = nowMs;
      this.applyControl(dtc, settings);
    }

    if (!this.manualClock && wall - this.lastPersist >= PERSIST_PERIOD_MS) {
      this.lastPersist = wall;
      void this.persist();
    }
    this.emit();
  }

  private stepPhysics(dt: number, powerW: number, s: Settings): void {
    const env = {
      gradePct: this.sample.gradePct * this.direction,
      altitudeM: this.sample.y,
      draft: this.draft,
    };
    this.v = stepSpeed(this.v, powerW, dt, { massKg: totalMassKg(s), cda: BIKE.cda }, env);
    if (this.routeFinished) this.v = 0;
    const ds = this.v * dt;
    this.distanceM += ds;
    this.courseD += ds * this.direction;
    const L = this.course.length;
    if (Number.isFinite(L)) {
      if (this.course.loop) {
        if (this.courseD >= L) {
          this.courseD -= L;
          this.routeLap++;
          this.recorder.lap('route', Date.now(), this.distanceM, this.movingSec);
        } else if (this.courseD < 0) {
          this.courseD += L;
        }
      } else if (this.courseD >= L || this.courseD <= 0) {
        this.courseD = Math.max(0, Math.min(L, this.courseD));
        if (!this.routeFinished && this.distanceM > 1) {
          this.routeFinished = true;
          this.v = 0;
        }
      }
    }
    this.sample = this.course.sample(this.courseD);
  }

  private applyControl(dt: number, s: Settings): void {
    const c = hub.controller;
    const mode = this.effectiveMode;
    const roadGrade = this.workoutDirective?.flatGrade ? 0 : this.sample.gradePct * this.direction;
    const rho = airDensity(this.sample.y);
    const cwEff = 0.5 * rho * BIKE.cda * (1 - this.draft);
    const vsAvailable = !!this.vs;
    if (mode === 'erg') {
      const base = this.workoutDirective?.targetW ?? this.ergTarget;
      this.ergFactor = this.ergProtection.update(dt, this.live.cadenceRpm);
      if (this.status === 'running') c.setErg(base * this.ergFactor);
      this.lastVs = null;
      return;
    }
    this.ergProtection.reset();
    this.ergFactor = 1;
    if (mode === 'vs' && vsAvailable) {
      const out = this.vs!.compute({
        dt,
        cadenceRpm: this.live.cadenceRpm,
        powerW: this.live.powerW,
        trainerSpeedKmh: this.live.speedKmh,
        gradePct: roadGrade,
        altitudeM: this.sample.y,
        draft: this.draft,
        settings: s,
      });
      this.lastVs = out;
      if (out.sim) c.setSim(out.sim);
      else if (out.ergW !== undefined) c.setErg(out.ergW);
      return;
    }
    // Plain SIM (SPEC §6.2): grade × trainer difficulty (also for negative grades), crr, cw ≈ ½·ρ·CdA.
    this.lastVs = null;
    c.setSim({
      gradePct: roadGrade * (s.trainerDifficultyPct / 100),
      crr: 0.004,
      cwKgPerM: cwEff,
      windMps: 0,
    });
  }

  private record(): void {
    const sm = this.sample;
    const vs = this.lastVs;
    const sample: RideSample = {
      t: Date.now(),
      el: Math.round(this.movingSec),
      power: Math.round(this.live.powerW),
      cadence: Math.round(this.live.cadenceRpm),
      hr: Math.round(this.live.hrBpm),
      speed: this.v,
      dist: this.distanceM,
      alt: sm.y,
      lat: sm.lat,
      lon: sm.lon,
      grade: sm.gradePct * this.direction,
      gear: this.vs?.gear ?? 0,
      mode: this.effectiveMode,
      draft: this.draft,
    };
    if (vs?.gSend !== undefined) sample.gSend = vs.gSend;
    if (vs?.pReq !== undefined) sample.pReq = vs.pReq;
    if (this.effectiveMode === 'erg') {
      sample.target = Math.round((this.workoutDirective?.targetW ?? this.ergTarget) * this.ergFactor);
    }
    this.recorder.add(sample);
  }

  snapshot(): ActiveRideSnapshot {
    const snap: ActiveRideSnapshot = {
      key: 'active',
      rideId: this.id,
      startedAt: this.startedAt,
      savedAt: Date.now(),
      samples: this.recorder.samples,
      laps: this.recorder.laps,
      distanceM: this.courseD,
      lap: this.routeLap,
    };
    if (this.course.route) snap.routeId = this.course.id;
    if (this.workout) {
      snap.workoutId = this.workout.workout.id;
      snap.workoutElapsedSec = this.workout.elapsedSec;
    }
    if (this.plannedId) snap.plannedId = this.plannedId;
    return snap;
  }

  async persist(): Promise<void> {
    try {
      await db.activeRide.put(this.snapshot());
    } catch (err) {
      console.warn('persist failed', err);
    }
  }

  view(): SessionSnapshotView {
    const v: SessionSnapshotView = {
      id: this.id,
      status: this.status,
      pauseReason: this.pauseReason,
      mode: this.mode,
      effectiveMode: this.effectiveMode,
      movingSec: this.movingSec,
      distanceM: this.distanceM,
      courseD: this.courseD,
      speedMps: this.v,
      powerW: this.live.powerW,
      power3s: this.power3s,
      cadenceRpm: this.live.cadenceRpm,
      hrBpm: this.live.hrBpm,
      altitudeM: this.sample.y,
      gradePct: this.sample.gradePct * this.direction,
      lap: this.recorder.lapCount,
      routeLap: this.routeLap,
      ergTarget: this.ergTarget,
      ergFactor: this.ergFactor,
      workoutTargetW: this.workoutDirective?.targetW ?? null,
      gear: this.vs?.gear ?? 0,
      ratio: this.vs?.ratio ?? 0,
      draft: this.draft,
      routeFinished: this.routeFinished,
      direction: this.direction,
      sample: this.sample,
      workoutErg: this.workoutErgEnabled,
    };
    if (this.workout) {
      v.workoutName = this.workout.workout.name;
      const wv = this.workout.view?.();
      if (wv) v.workout = wv;
    }
    if (this.lastVs?.gSend !== undefined) v.gSend = this.lastVs.gSend;
    if (this.lastVs?.pReq !== undefined) v.pReq = this.lastVs.pReq;
    return v;
  }

  /** Stop the loop; the caller (manager) saves the ride. */
  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.status !== 'finished') {
      this.recorder.close(Date.now(), this.distanceM, this.movingSec);
      this.status = 'finished';
    }
    for (const f of this.finishListeners) f();
    this.emit();
  }

  private emit(): void {
    for (const l of this.listeners) l(this);
  }
}
